// Types for the Production Module
// Handles recipes (bill of materials) and production batches
import { z } from "zod";

import type { Product } from "./product";
import type { OilType, RawMaterial } from "./purchasing";

// =============================================================================
// Enums & Literal Types
// =============================================================================

export type ProductionBatchStatus =
  | "planned"
  | "in_progress"
  | "completed"
  | "cancelled";

// =============================================================================
// Database Entities
// =============================================================================

export interface AssemblyLine {
  id: string;
  code: string; // e.g. "L15"
  name: string | null; // e.g. "Línea 15"
  plant: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
}

export interface AssemblyLineInput {
  code: string;
  name?: string | null;
  plant?: string | null;
  is_active?: boolean;
  sort_order?: number;
}

export interface SkuRecipe {
  id: string;
  product_id: string;
  oil_type_id: string | null; // Optional - oils can be mixed via ingredients
  name: string;
  is_default: boolean;
  description: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  // Joined relations
  product?: Product;
  oil_type?: OilType;
  ingredients?: RecipeIngredient[];
}

export interface RecipeIngredient {
  id: string;
  recipe_id: string;
  raw_material_id: string | null;
  oil_type_id: string | null;
  quantity_per_box: number;
  waste_factor: number;
  notes: string | null;
  created_at: string;
  // Joined relations
  raw_material?: RawMaterial;
  oil_type?: OilType;
}

export interface ProductionBatch {
  id: string;
  batch_number: string;
  product_id: string;
  recipe_id: string | null;
  target_quantity: number;
  actual_quantity: number | null;
  start_date: string;
  end_date: string;
  expiry_date: string;
  status: ProductionBatchStatus;
  // Assembly line, plant, and lot number for pallet tracking
  assembly_line: string | null;
  plant: string | null;
  lot_number: string | null;
  // Allergen tracking
  contains_allergens: boolean;
  // Production unit type
  production_unit: string;
  // Cost tracking
  exchange_rate_id: string | null;
  exchange_rate_snapshot: number | null;
  estimated_cost_clp: number | null;
  estimated_cost_usd: number | null;
  actual_cost_clp: number | null;
  actual_cost_usd: number | null;
  notes: string | null;
  created_by: string | null;
  completed_by: string | null;
  created_at: string;
  updated_at: string;
  // Joined relations
  product?: Product;
  recipe?: SkuRecipe;
  ingredients?: ProductionBatchIngredient[];
  created_by_profile?: { name: string; lastname: string };
  completed_by_profile?: { name: string; lastname: string };
}

export interface ProductionBatchIngredient {
  id: string;
  production_batch_id: string;
  recipe_ingredient_id: string | null; // Links to original recipe ingredient for audit trail
  raw_material_id: string | null;
  oil_type_id: string | null;
  planned_quantity: number;
  actual_quantity: number | null;
  unit_cost_usd: number | null;
  unit_cost_clp: number;
  exchange_rate_at_batch: number | null;
  is_override: boolean;
  notes: string | null;
  created_at: string;
  // Joined relations
  raw_material?: RawMaterial;
  oil_type?: OilType;
}

// =============================================================================
// View Types (from database views)
// =============================================================================

export interface CurrentOilPrice {
  oil_type_id: string;
  oil_type_name: string;
  density_kg_per_liter: number;
  liters_per_tonne: number;
  price_per_tonne: number | null;
  currency: string | null;
  effective_date: string | null;
  cost_per_liter_usd: number | null;
  cost_per_liter_clp: number | null;
}

export interface CurrentMaterialPrice {
  raw_material_id: string;
  material_name: string;
  unit: string;
  current_stock: number;
  current_price: number | null;
  currency: string | null;
  effective_date: string | null;
}

export interface ProductionBatchSummary {
  id: string;
  batch_number: string;
  status: ProductionBatchStatus;
  product_name: string;
  product_sku: string | null;
  production_unit: string;
  assembly_line: string | null;
  plant: string | null;
  target_quantity: number;
  actual_quantity: number | null;
  start_date: string;
  end_date: string;
  expiry_date: string;
  exchange_rate_snapshot: number | null;
  estimated_cost_clp: number | null;
  estimated_cost_usd: number | null;
  actual_cost_clp: number | null;
  actual_cost_usd: number | null;
  contains_allergens: boolean;
  cost_per_box_clp: number | null;
  created_by_name: string | null;
  created_at: string;
}

// =============================================================================
// Form Data Types
// =============================================================================

export interface RecipeIngredientFormData {
  id?: string;
  raw_material_id?: string;
  oil_type_id?: string;
  quantity_per_box: number;
  waste_factor: number;
  notes?: string;
}

export interface RecipeFormData {
  product_id: string;
  name: string;
  is_default: boolean;
  description?: string;
  ingredients: RecipeIngredientFormData[];
}

export interface BatchIngredientOverride {
  recipe_ingredient_id?: string; // ID from recipe_ingredients, null if new ingredient
  raw_material_id?: string;
  oil_type_id?: string;
  quantity_per_box: number;
  waste_factor: number;
  notes?: string;
  is_removed?: boolean; // True if ingredient was removed from recipe
}

export interface ProductionBatchFormData {
  product_id: string;
  recipe_id?: string;
  target_quantity: number;
  start_date: string;
  end_date: string;
  expiry_date: string;
  assembly_line?: string;
  plant?: string;
  contains_allergens?: boolean;
  production_unit?: string; // Copied from product at creation time
  notes?: string;
  ingredient_overrides?: BatchIngredientOverride[]; // Custom ingredients for this batch
}

export interface BatchIngredientActualData {
  id: string;
  actual_quantity: number;
  oil_type_id?: string;
  raw_material_id?: string;
}

export interface BatchCompletionData {
  actual_quantity: number;
  actual_cost_clp: number;
  actual_cost_usd: number;
  completed_by: string;
  ingredients: BatchIngredientActualData[];
  notes?: string;
}

// New-inventory production accounting (Aliace Pruebas test company). `eligible`
// means the batch's finished good is a test-company inv product; the asiento ids
// are set once the batch has been contabilized.
export interface ProductionBatchAccounting {
  eligible: boolean;
  consumption_asiento_id: string | null;
  output_asiento_id: string | null;
}

// =============================================================================
// Filter Types
// =============================================================================

export interface ProductionBatchFilters {
  status?: ProductionBatchStatus | ProductionBatchStatus[];
  product_id?: string;
  start_date_from?: string;
  start_date_to?: string;
  search?: string;
  assembly_line?: string;
  plant?: string;
}

export interface RecipeFilters {
  product_id?: string;
  is_default?: boolean;
  search?: string;
}

// =============================================================================
// Calculated Types
// =============================================================================

export interface RecipeCostBreakdown {
  recipe_id: string;
  oil_cost_usd: number;
  oil_cost_clp: number;
  material_cost_clp: number;
  total_cost_clp: number;
  total_cost_usd: number;
  exchange_rate: number;
  ingredients: {
    name: string;
    type: "oil" | "material";
    quantity: number;
    quantity_with_waste: number;
    unit: string;
    unit_cost: number;
    total_cost: number;
  }[];
}

// =============================================================================
// Display Helpers
// =============================================================================

export const PRODUCTION_BATCH_STATUS_LABELS: Record<
  ProductionBatchStatus,
  string
> = {
  planned: "Planificado",
  in_progress: "En Progreso",
  completed: "Completado",
  cancelled: "Cancelado",
};

export const PRODUCTION_BATCH_STATUS_COLORS: Record<
  ProductionBatchStatus,
  string
> = {
  planned: "bg-yellow-100 text-yellow-800",
  in_progress: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-gray-100 text-gray-800",
};

export const getStatusBadgeClass = (status: ProductionBatchStatus): string => {
  return PRODUCTION_BATCH_STATUS_COLORS[status] || "bg-gray-100 text-gray-800";
};

export const formatBatchNumber = (batchNumber: string): string => {
  // ACEITE500-0106-0110 → ACEITE500 / 06-Ene al 10-Ene
  const parts = batchNumber.split("-");
  if (parts.length >= 3) {
    return `${parts[0]} (${parts[1]}-${parts[2]})`;
  }
  return batchNumber;
};

// =============================================================================
// Sobreconsumo (over-consumption) & Merma (waste) — consumption adjustments
// =============================================================================

export type IngredientKind = "oil" | "raw_material";
export type IngredientPlant = "P1" | "P2";

// --- Sobreconsumo (over-consumption on a completed lote → cost INTO the PT) ---
export const overconsumptionSchema = z
  .object({
    ingredient_kind: z.enum(["oil", "raw_material"]),
    oil_type_id: z.string().uuid().optional(),
    raw_material_id: z.string().uuid().optional(),
    extra_quantity: z.number().positive("La cantidad debe ser mayor a 0"),
    unit: z.string(),
    plant: z.enum(["P1", "P2"]),
    notes: z.string().optional(),
  })
  .refine(
    (v) =>
      v.ingredient_kind === "oil" ? !!v.oil_type_id : !!v.raw_material_id,
    { message: "Debe seleccionar un ingrediente", path: ["oil_type_id"] }
  );
export type OverconsumptionFormData = z.infer<typeof overconsumptionSchema>;

export interface OverconsumptionInput {
  batch_id: string;
  ingredient_kind: IngredientKind;
  oil_type_id?: string | null;
  raw_material_id?: string | null;
  extra_quantity: number;
  unit: string;
  plant: string;
  notes?: string | null;
}

// --- Merma (ingredient waste/loss → cost NOT into the PT) ---
// One waste line (ingredient + quantity). Plant + reason are shared per event.
export interface MermaLineInput {
  ingredient_kind: IngredientKind;
  oil_type_id?: string | null;
  raw_material_id?: string | null;
  quantity: number;
  unit: string;
}

// A whole merma event: shared plant + reason, one or more ingredient lines.
export interface MermaBatchInput {
  plant: string;
  reason: string;
  batch_id?: string | null;
  lines: MermaLineInput[];
}

// Result echoed by the record RPCs (drives the success toast).
export interface ConsumptionAdjustmentResult {
  quantity: number;
  unit_cost_clp: number;
  total_cost_clp: number;
}

// Aggregate result of record_ingredient_mermas (one entry per line).
export interface MermaBatchResult {
  count: number;
  total_cost_clp: number;
  lines: ConsumptionAdjustmentResult[];
}

// Row from get_ingredient_mermas — powers the Mermas registry page.
export interface MermaRecord {
  id: string;
  movement_date: string;
  item_kind: IngredientKind;
  oil_type_id: string | null;
  raw_material_id: string | null;
  item_code: string | null;
  item_name: string | null;
  item_unit: string | null;
  density_kg_per_liter: number | null;
  quantity: number; // ledger unit: liters for oils, base unit for materials
  unit_cost_clp: number;
  total_cost_clp: number;
  plant: string | null;
  batch_id: string | null;
  batch_number: string | null;
  reason: string | null;
  created_by_name: string | null;
}

// Row from get_batch_overconsumptions — powers the Sobreconsumos tab of Ajustes de Consumo.
// Unlike mermas, oil quantities are already in kg (no liter conversion needed), and there is
// no user column (production_batch_ingredients doesn't record who registered it).
export interface OverconsumptionRecord {
  id: string; // production_batch_ingredients id (what reverse_batch_overconsumption takes)
  movement_date: string; // registration timestamp (pbi.created_at)
  batch_period: string; // YYYY-MM from the lote's end date — gates Revertir (period open check)
  item_kind: IngredientKind;
  oil_type_id: string | null;
  raw_material_id: string | null;
  item_code: string | null;
  item_name: string | null;
  item_unit: string | null; // 'kg' for oils, base unit for materials
  quantity: number;
  unit_cost_clp: number;
  total_cost_clp: number;
  plant: string | null;
  batch_id: string;
  batch_number: string | null;
  product_code: string | null;
  product_name: string | null;
  reason: string | null;
}
